import Store from 'store/store';
import { accessNested } from 'utils/utils';

export function getDeviceInfo() {
	const dimensions = getDims();
	const pixelDimensions = getPixelDims();
	const device = getDevice();
	const deviceSize = getSize(dimensions, device);
	const deviceWidthSize = getSize(dimensions, device, true);
	const orientation = getOrientation(dimensions);
	// TODO: get rid of browser detection
	const isIOS = getIsIOS();
	const isEdge = getIsEdge();
	const isSafari = getIsSafari();
	const isDesktopSize = getIsDesktopSize();
	const isDesktopWidth = getIsDesktopWidth();
	const isSocialWebview = getIsSocialWebview();
	return {
		dimensions,
		pixelDimensions,
		device,
		deviceSize,
		deviceWidthSize,
		orientation,
		isIOS,
		isEdge,
		isSafari,
		isDesktopSize,
		isDesktopWidth,
		isSocialWebview
	};
}

export function getDims() {
	return { width: window.innerWidth, height: window.innerHeight };
}

export function getPixelDims() {
	const pixelRatio = window.devicePixelRatio || 1;
	return { width: window.innerWidth * pixelRatio, height: window.innerHeight * pixelRatio };
}

export function getDevice() {
	if (typeof window !== 'undefined') {
		const agent = ('' + navigator.userAgent).toLowerCase();
		if (agent.indexOf('android') !== -1) {
			return 'android';
		}
		if (agent.indexOf('iphone') !== -1) {
			return 'iphone';
		}
		if (agent.indexOf('ipad') !== -1) {
			return 'ipad'; //ipad no longer seems to always appear in user agent
		}
	}
	return navigator.maxTouchPoints > 1 ? 'tablet' : 'desktop';
}

export function getSize(dimensions = getDims(), device = getDevice(), byWidthOnly) {
	// Bypass checks for iPhone as iPhone X is large
	if (device == 'iphone') {
		return 'mobile';
	}

	const store = Store.get();
	const breakTablet = accessNested(store, 'appData.styling.variables.breakTablet', '767').replace('px', '');
	const breakSmallDesktop = accessNested(store, 'appData.styling.variables.breakSmallDesktop', '1024').replace('px', '');
	const breakDesktop = accessNested(store, 'appData.styling.variables.breakDesktop', '1280').replace('px', '');
	const breakLargeDesktop = accessNested(store, 'appData.styling.variables.breakLargeDesktop', '1600').replace('px', '');

	// List of breakpoints (used if width is stritcly lower than the value)
	const breakpoints = [[+breakTablet, 'mobile'], [+breakSmallDesktop, 'tablet'], [+breakDesktop, 'smallDesktop'], [+breakLargeDesktop, 'desktop'], [Infinity, 'largeDesktop']];

	for (let i = 0; i < breakpoints.length; i++) {
		let [breakpoint, name] = breakpoints[i];
		if (dimensions.width >= breakpoint) {
			continue;
		}

		// return tablet if small desktop but portrait
		if (name === 'smallDesktop' && dimensions.width < dimensions.height && !byWidthOnly) {
			return 'tablet';
		}

		// recognise a mobile when its long and in landscape mode
		if (name !== 'mobile' && device !== 'tablet' && device !== 'desktop' && getOrientation({ width: dimensions.width, height: dimensions.height }) === 'landscape' && (dimensions.height < breakTablet) && !byWidthOnly) {
			return 'mobile';
		}
		return name;
	}

	return 'unknown';
}

export function getOrientation({ width, height }) {
	return width > height ? 'landscape' : 'portrait';
}

export function getIsDesktopSize(size = getSize()) {
	const orientation = getOrientation(getDims());
	return (size === 'desktop' || size === 'largeDesktop' || (size === 'smallDesktop' && orientation === 'landscape'));
}

export function getIsDesktopWidth(size = getSize(getDims(), getDevice(), true)) {
	return size.toLowerCase().includes('desktop');
}

export function getIsIOS() {
	if (typeof window === 'undefined') {
		return false;
	}
	const agent = ('' + navigator.userAgent).toLowerCase();
	const isIpad = /Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
	return agent.indexOf('ipad') !== -1 || isIpad || agent.indexOf('iphone') !== -1;
}

export function getIsEdge() {
	const ua = navigator.userAgent || '';
	// Chromium edge uses "Edg" - but we might want to treat it differently because different engine
	const edge = ua.match(/Edge\/(\d+)\./);
	if (edge) {
		return +edge[1];
	}
	return false;
}

export function getIsSafari() {
	return /^((?!chrome|android|CriOS|fxios).)*safari/i.test(navigator.userAgent);
}

export function isIframed() {
	return window.top !== window.self || /[?&]iframe([=&]|$)/i.test(location.search);
}

// check if it is a standalone home ent / ticketing page iframed on another site.
// should be iframed and also have the query string showbarebones
export function getIsSinglePageIframed() {
	if (accessNested(Store.get(), 'appData.options.bareboneShowtimesView', false)) {
		return true;
	}

	if (typeof window === 'undefined') {
		return false;
	}
	const isSinglePage = /[?&]showbarebones([=&]|$)/i.test(location.search);
	return isIframed() && isSinglePage;
}

export function getIsShowtimesOnlyIframed() {
	if (accessNested(Store.get(), 'appData.options.bareboneShowtimesView', false)) {
		return true;
	}

	if (typeof window === 'undefined') {
		return false;
	}
	const isSinglePage = /[?&]showbarebones=showtimes([&]|$)/i.test(location.search);
	return isIframed() && isSinglePage;
}

export function getIsSocialWebview() {
	const agent = (navigator.userAgent || '').toLowerCase();
	return ['instagram', 'webview', 'fban', 'fbav', 'tiktok'].some(type => agent.indexOf(type) !== -1);
}

export function hideFooter() {
	if (typeof window === 'undefined') {
		return false;
	}
	const hideFooter = /[?&]hideFooter([=&]|$)/i.test(location.search);
	return hideFooter;
}